import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export function normalizeEmailVerificationHashRoute() {
  const prefix = '#verify-email?';
  if (!window.location.hash.startsWith(prefix)) return false;

  const token = new URLSearchParams(window.location.hash.slice(prefix.length)).get('token');
  if (!token) return false;

  window.history.replaceState(null, '', `/verify-email#token=${encodeURIComponent(token)}`);
  return true;
}

function VerifyEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const [verificationToken] = useState(() => new URLSearchParams(location.hash.replace(/^#/, '')).get('token') || '');
  const [email, setEmail] = useState(location.state?.email || '');
  const [state, setState] = useState(verificationToken ? 'verifying' : 'pending');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const hasSubmittedToken = useRef(false);

  useEffect(() => {
    // Keep bearer tokens out of browser history after reading the fragment.
    navigate('/verify-email', { replace: true, state: location.state });
    if (!verificationToken || hasSubmittedToken.current) return;
    hasSubmittedToken.current = true;
    const confirm = async () => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/auth/email-verification/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: verificationToken }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Unable to verify this email link');
        setMessage(data.message);
        setState('verified');
      } catch (requestError) {
        setError(requestError.message || 'This email verification link is invalid or expired.');
        setState('invalid');
      }
    };
    confirm();
  }, [location.state, navigate, verificationToken]);

  const handleResend = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setResending(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/auth/email-verification/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to resend verification email');
      setMessage(data.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setResending(false);
    }
  };

  const loginState = {
    email,
    afterAuthPath: location.state?.afterAuthPath,
    marketingRegistration: Boolean(location.state?.marketingRegistration),
  };

  return (
    <div className="public-page">
      <div className="login-section">
        <div className="login-container password-reset-container">
          <div className="login-card">
            <h1 className="login-title">Verify Your Email</h1>
            {state === 'verifying' && <p className="login-subtitle">Verifying your secure email link…</p>}
            {state === 'verified' && (
              <>
                <div className="admin-quality-success" role="status">Your email is verified.</div>
                <p className="login-footer-text"><Link className="login-button login-button-link" to="/login" state={loginState}>Continue to Sign In</Link></p>
              </>
            )}
            {state === 'invalid' && (
              <div className="login-error" role="alert">{error || 'This email verification link is invalid or expired.'}</div>
            )}
            {state === 'pending' && (
              <p className="login-subtitle">Check your email for a verification link before signing in.</p>
            )}
            {(state === 'pending' || state === 'invalid') && (
              <form className="login-form" onSubmit={handleResend}>
                <div className="login-form-group">
                  <label className="login-label" htmlFor="verification-email">Email</label>
                  <input
                    id="verification-email"
                    className="login-input"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
                <button className="login-button" type="submit" disabled={resending}>
                  {resending ? 'Sending…' : 'Resend Verification Email'}
                </button>
              </form>
            )}
            {message && state !== 'verified' && <div className="admin-quality-success" role="status">{message}</div>}
            {state !== 'verified' && (
              <div className="login-footer">
                <p className="login-footer-text"><Link className="login-signup-link" to="/login" state={loginState}>Back to sign in</Link></p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
